/**
 * Shared "Mes fichiers" library modal (same backend as Amalgame / Fast Order).
 *
 * Usage:
 *   const lib = window.MesFichiersLibrary.mount({
 *     root,                 // element with data-customer-id / data-library-* urls
 *     overlay,              // .mf-library-overlay
 *     openButton,           // optional trigger
 *     addLabel: 'Ouvrir',
 *     onImport: async (file, { fileMeta }) => { ... },
 *     toast: (message, tone) => {},
 *   });
 */
(function () {
  'use strict';

  function mount(options = {}) {
    const root = options.root;
    const overlay = options.overlay;
    if (!root || !overlay) {
      console.warn('[MesFichiersLibrary] root/overlay missing');
      return null;
    }
    if (overlay.__mfLibraryBound) return overlay.__mfLibraryBound;

    const config = {
      customerId: String(root.dataset.customerId || options.customerId || ''),
      isLoggedIn: root.dataset.customerLoggedIn === 'true' || options.isLoggedIn === true,
      listUrl: root.dataset.libraryListUrl || options.listUrl || '/apps/easy-order/file-storage/list',
      uploadUrl: root.dataset.libraryUploadUrl || options.uploadUrl || '/apps/easy-order/file-storage/upload',
      deleteUrl: root.dataset.libraryDeleteUrl || options.deleteUrl || '/apps/easy-order/file-storage/delete',
      loginUrl: root.dataset.loginUrl || options.loginUrl || '/account/login',
      addLabel: options.addLabel || 'Ajouter',
      toast: typeof options.toast === 'function' ? options.toast : (msg) => window.alert(msg),
      onImport: typeof options.onImport === 'function' ? options.onImport : null,
    };

    const state = {
      files: [],
      loaded: false,
      loading: false,
      uploading: false,
      deletingKeys: new Set(),
    };

    const refs = {
      overlay,
      grid: overlay.querySelector('[data-mf-library-grid]'),
      search: overlay.querySelector('[data-mf-library-search]'),
      status: overlay.querySelector('[data-mf-library-status]'),
      loading: overlay.querySelector('[data-mf-library-loading]'),
      error: overlay.querySelector('[data-mf-library-error]'),
      empty: overlay.querySelector('[data-mf-library-empty]'),
      noResults: overlay.querySelector('[data-mf-library-no-results]'),
      uploadTrigger: overlay.querySelector('[data-mf-library-upload-trigger]'),
      uploadInput: overlay.querySelector('[data-mf-library-upload-input]'),
      retry: overlay.querySelector('[data-mf-library-retry]'),
      closeButtons: Array.from(overlay.querySelectorAll('[data-mf-library-close]')),
    };

    function setVisible(el, visible) {
      if (!el) return;
      el.classList.toggle('mf-library-visuallyhidden', !visible);
    }

    function setStatus(message = '', type = '') {
      if (!refs.status) return;
      refs.status.textContent = message;
      refs.status.classList.toggle('is-error', type === 'error');
      refs.status.classList.toggle('is-success', type === 'success');
      refs.status.classList.toggle('is-loading', type === 'loading');
    }

    function normalizeSearch(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    }

    function filenameOf(file) {
      const original = file?.metadata?.original_filename;
      if (original) return String(original);
      return String(file?.filename || file?.key || 'Fichier').replace(/^\d+_[a-f0-9-]{16,}_/i, '');
    }

    function extensionOf(filename, contentType) {
      const match = String(filename || '').match(/\.([a-z0-9]+)$/i);
      if (match) return match[1].toUpperCase();
      if (contentType === 'application/pdf') return 'PDF';
      if (contentType === 'image/png') return 'PNG';
      return 'FILE';
    }

    function formatBytes(value) {
      const bytes = Number(value);
      if (!Number.isFinite(bytes) || bytes < 0) return '';
      if (bytes < 1024) return `${bytes} o`;
      if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
      const mb = bytes / (1024 * 1024);
      return `${mb.toFixed(mb >= 10 ? 0 : 1).replace('.', ',')} Mo`;
    }

    function formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      try {
        return new Intl.DateTimeFormat('fr', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(date);
      } catch (_) {
        return date.toLocaleDateString('fr');
      }
    }

    function isSafeImageUrl(url) {
      try {
        const parsed = new URL(String(url || ''), window.location.origin);
        return parsed.protocol === 'https:' || parsed.protocol === 'http:';
      } catch (_) {
        return false;
      }
    }

    function thumbnailUrl(file) {
      const url = file?.thumbnail?.download_url || file?.thumbnail?.url || file?.thumbnail_url || '';
      return isSafeImageUrl(url) ? String(url) : '';
    }

    function isRasterPreviewFile(file, name = '') {
      const contentType = String(file?.content_type || '').toLowerCase();
      if (
        contentType.includes('png') ||
        contentType.includes('jpeg') ||
        contentType.includes('jpg') ||
        contentType.includes('webp') ||
        contentType.includes('gif')
      ) {
        return true;
      }
      return /\.(png|jpe?g|webp|gif)$/i.test(String(name || ''));
    }

    // Server thumbnails are often baked into a square and look stretched.
    // Prefer the original raster file for previews.
    function previewUrl(file, name = '') {
      if (isRasterPreviewFile(file, name)) {
        const original =
          file?.download_url || file?.url || file?.file_url || file?.original_url || '';
        if (isSafeImageUrl(original)) return String(original);
      }
      return thumbnailUrl(file);
    }

    function fitLibraryPreviewImage(image, preview) {
      if (!image || !preview) return;
      const naturalWidth = Number(image.naturalWidth) || 0;
      const naturalHeight = Number(image.naturalHeight) || 0;
      if (naturalWidth <= 0 || naturalHeight <= 0) return;
      const maxWidth = Math.max(24, preview.clientWidth - 20);
      const maxHeight = Math.max(24, preview.clientHeight - 20);
      const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
      const width = Math.max(1, Math.round(naturalWidth * scale));
      const height = Math.max(1, Math.round(naturalHeight * scale));
      image.width = width;
      image.height = height;
      image.style.width = `${width}px`;
      image.style.height = `${height}px`;
      image.style.maxWidth = 'none';
      image.style.maxHeight = 'none';
    }

    async function parseJson(response) {
      const text = await response.text();
      if (!text) return {};
      try {
        return JSON.parse(text);
      } catch (_) {
        throw new Error(text || `HTTP ${response.status}`);
      }
    }

    function open(event) {
      if (event) event.preventDefault?.();
      if (!config.isLoggedIn || !config.customerId) {
        config.toast('Connectez-vous pour accéder à vos fichiers', 'error');
        const returnTo = encodeURIComponent(window.location.href);
        const sep = config.loginUrl.includes('?') ? '&' : '?';
        window.location.href = `${config.loginUrl}${sep}return_url=${returnTo}`;
        return;
      }
      overlay.classList.add('is-open');
      loadFiles();
      requestAnimationFrame(() => refs.search?.focus());
    }

    function close(event) {
      if (event && event.type === 'click' && event.target !== event.currentTarget) return;
      overlay.classList.remove('is-open');
    }

    function filteredFiles() {
      const query = normalizeSearch(refs.search?.value);
      if (!query) return state.files;
      return state.files.filter((file) => normalizeSearch(filenameOf(file)).includes(query));
    }

    function createCard(file) {
      const filename = filenameOf(file);
      const extension = extensionOf(filename, file?.content_type);
      const size = formatBytes(file?.size);
      const date = formatDate(file?.last_modified);
      const width = file?.metadata?.width_mm;
      const height = file?.metadata?.height_mm;
      const thumb = thumbnailUrl(file);
      const previewSrc = previewUrl(file, filename);
      const key = String(file?.key || '');

      const card = document.createElement('article');
      card.className = 'mf-library-card';
      card.dataset.fileKey = key;
      card.dataset.filename = filename;
      card.dataset.contentType = String(file?.content_type || '');

      const preview = document.createElement('div');
      preview.className = 'mf-library-preview';
      if (previewSrc) {
        const img = document.createElement('img');
        img.src = previewSrc;
        img.alt = `Aperçu de ${filename}`;
        img.loading = 'lazy';
        img.decoding = 'async';
        img.referrerPolicy = 'no-referrer';
        img.draggable = false;
        img.addEventListener('load', () => fitLibraryPreviewImage(img, preview));
        img.addEventListener('error', () => {
          if (thumb && img.src !== thumb && !img.dataset.thumbFallback) {
            img.dataset.thumbFallback = '1';
            img.src = thumb;
            return;
          }
          img.remove();
          preview.appendChild(
            Object.assign(document.createElement('span'), {
              className: 'mf-library-preview-placeholder',
              textContent: extension,
            })
          );
        });
        preview.appendChild(img);
      } else {
        preview.appendChild(
          Object.assign(document.createElement('span'), {
            className: 'mf-library-preview-placeholder',
            textContent: extension,
          })
        );
      }

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'mf-library-delete';
      del.title = 'Supprimer';
      del.setAttribute('aria-label', `Supprimer ${filename}`);
      del.innerHTML = '<span>&times;</span>';
      del.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        deleteFile(key, card, del);
      });
      preview.appendChild(del);

      const body = document.createElement('div');
      body.className = 'mf-library-card-body';

      const name = document.createElement('h3');
      name.className = 'mf-library-filename';
      name.textContent = filename;
      name.title = filename;
      body.appendChild(name);

      if (date) {
        body.appendChild(
          Object.assign(document.createElement('p'), {
            className: 'mf-library-meta',
            textContent: `Importé le ${date}`,
          })
        );
      }
      const details = [size, extension].filter(Boolean).join(' · ');
      if (details) {
        body.appendChild(
          Object.assign(document.createElement('p'), {
            className: 'mf-library-meta',
            textContent: details,
          })
        );
      }
      if (width && height) {
        body.appendChild(
          Object.assign(document.createElement('p'), {
            className: 'mf-library-meta',
            textContent: `${width} × ${height} mm`,
          })
        );
      }

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'mf-library-add';
      add.textContent = config.addLabel;
      add.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        addToEditor(card);
      });
      body.appendChild(add);

      card.append(preview, body);
      return card;
    }

    async function requestDelete(key) {
      if (!config.customerId || !config.deleteUrl || !key) {
        throw new Error('Informations de suppression incomplètes.');
      }
      const attempts = [
        { query: { user_id: config.customerId }, body: { user_id: config.customerId, file_key: key } },
        { query: { user_id: config.customerId, file_key: key }, body: null },
      ];
      let lastError = null;
      for (const attempt of attempts) {
        const url = new URL(config.deleteUrl, window.location.origin);
        Object.entries(attempt.query).forEach(([name, value]) => url.searchParams.set(name, value));
        const response = await fetch(url.toString(), {
          method: 'DELETE',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            ...(attempt.body ? { 'Content-Type': 'application/json' } : {}),
          },
          ...(attempt.body ? { body: JSON.stringify(attempt.body) } : {}),
        });
        const json = await parseJson(response).catch(() => ({}));
        if (response.ok || response.status === 404) return json;
        lastError = new Error(json?.message || json?.error || `Suppression impossible (HTTP ${response.status}).`);
        if (response.status !== 400 && response.status !== 422) throw lastError;
      }
      throw lastError || new Error('Impossible de supprimer ce fichier.');
    }

    async function deleteFile(key, card, button) {
      const fileKey = String(key || '');
      if (!fileKey || state.deletingKeys.has(fileKey)) return;
      const filename =
        card?.dataset?.filename ||
        filenameOf(state.files.find((item) => String(item?.key || '') === fileKey)) ||
        'ce fichier';
      if (!window.confirm(`Supprimer définitivement « ${filename} » de votre bibliothèque ?`)) return;

      state.deletingKeys.add(fileKey);
      card?.classList.add('is-busy', 'is-deleting');
      if (button) {
        button.disabled = true;
        button.classList.add('is-loading');
      }
      setStatus(`Suppression de ${filename}…`, 'loading');

      try {
        await requestDelete(fileKey);
        state.files = state.files.filter((item) => String(item?.key || '') !== fileKey);
        renderGrid();
        setStatus('Fichier supprimé.', 'success');
        config.toast(`${filename} supprimé`, 'good');
      } catch (error) {
        console.error('[MesFichiersLibrary] delete error:', fileKey, error);
        const message = error?.message || 'Impossible de supprimer ce fichier';
        setStatus(message, 'error');
        config.toast(message, 'error');
        card?.classList.remove('is-busy', 'is-deleting');
        if (button) {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      } finally {
        state.deletingKeys.delete(fileKey);
      }
    }

    function renderGrid() {
      if (!refs.grid) return;
      refs.grid.querySelectorAll('.mf-library-card').forEach((card) => card.remove());

      const list = filteredFiles();
      const hasFiles = state.files.length > 0;
      const hasMatches = list.length > 0;
      const query = normalizeSearch(refs.search?.value);

      setVisible(refs.empty, !hasFiles && !state.loading);
      setVisible(refs.noResults, hasFiles && Boolean(query) && !hasMatches);
      setVisible(refs.grid, true);

      const fragment = document.createDocumentFragment();
      list.forEach((file) => fragment.appendChild(createCard(file)));
      refs.grid.insertBefore(fragment, refs.uploadTrigger || null);
    }

    async function loadFiles({ force = false } = {}) {
      if (!config.customerId || state.loading) return;
      if (state.loaded && !force) {
        renderGrid();
        return;
      }

      setStatus('');
      setVisible(refs.error, false);
      setVisible(refs.empty, false);
      setVisible(refs.noResults, false);
      state.loading = true;
      setVisible(refs.loading, true);
      if (refs.search) refs.search.disabled = true;
      if (refs.uploadTrigger) refs.uploadTrigger.disabled = true;

      try {
        const url = new URL(config.listUrl, window.location.origin);
        url.searchParams.set('user_id', config.customerId);
        const response = await fetch(url.toString(), {
          method: 'GET',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const json = await parseJson(response);
        if (!response.ok) throw new Error(json?.message || `HTTP ${response.status}`);

        const files = Array.isArray(json?.files)
          ? json.files
          : Array.isArray(json?.data?.files)
            ? json.data.files
            : [];

        state.files = files.sort(
          (a, b) => new Date(b?.last_modified || 0) - new Date(a?.last_modified || 0)
        );
        state.loaded = true;
        renderGrid();
      } catch (error) {
        console.error('[MesFichiersLibrary] list error:', error);
        setVisible(refs.error, true);
        setVisible(refs.grid, false);
        setStatus(error?.message || 'Chargement impossible', 'error');
      } finally {
        state.loading = false;
        setVisible(refs.loading, false);
        if (refs.search) refs.search.disabled = false;
        if (refs.uploadTrigger) refs.uploadTrigger.disabled = state.uploading;
      }
    }

    async function uploadFile(file, meta = {}) {
      if (!config.customerId) throw new Error('Connectez-vous pour utiliser Mes fichiers');
      if (!file) throw new Error('Fichier manquant');
      const ext = String(file.name || '').split('.').pop().toLowerCase();
      if (!['png', 'pdf', 'ai'].includes(ext)) {
        throw new Error('Formats acceptés : PNG, PDF et AI.');
      }

      const url = new URL(config.uploadUrl, window.location.origin);
      url.searchParams.set('user_id', config.customerId);
      const formData = new FormData();
      formData.append('file', file, file.name);
      const widthMm = Number(meta.widthMm);
      const heightMm = Number(meta.heightMm);
      if (Number.isFinite(widthMm) && widthMm > 0) formData.append('width_mm', String(widthMm));
      if (Number.isFinite(heightMm) && heightMm > 0) formData.append('height_mm', String(heightMm));

      const response = await fetch(url.toString(), {
        method: 'POST',
        credentials: 'same-origin',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      const json = await parseJson(response);
      if (!response.ok) throw new Error(json?.message || `HTTP ${response.status}`);
      return json;
    }

    async function uploadFiles(fileList) {
      const selected = Array.from(fileList || []);
      if (!selected.length || state.uploading) return;

      const allowed = new Set(['png', 'pdf', 'ai']);
      const invalid = selected.filter(
        (file) => !allowed.has(String(file.name || '').split('.').pop().toLowerCase())
      );
      if (invalid.length) {
        setStatus('Formats acceptés : PNG, PDF et AI.', 'error');
        return;
      }

      state.uploading = true;
      if (refs.uploadTrigger) refs.uploadTrigger.disabled = true;

      const failed = [];
      try {
        for (let i = 0; i < selected.length; i += 1) {
          const file = selected[i];
          setStatus(`Import ${i + 1}/${selected.length} : ${file.name}`, 'loading');
          try {
            await uploadFile(file);
          } catch (error) {
            console.error('[MesFichiersLibrary] upload error:', file.name, error);
            failed.push(file.name);
          }
        }
        await loadFiles({ force: true });
        setStatus(
          failed.length
            ? `${failed.length} fichier(s) n'ont pas pu être importé(s).`
            : selected.length > 1
              ? 'Fichiers ajoutés à la bibliothèque.'
              : 'Fichier ajouté à la bibliothèque.',
          failed.length ? 'error' : 'success'
        );
      } finally {
        state.uploading = false;
        if (refs.uploadTrigger) refs.uploadTrigger.disabled = false;
      }
    }

    function originalUrl(file) {
      return String(file?.download_url || '').trim();
    }

    function knownThumbnailUrl(fileMeta) {
      return String(
        fileMeta?.thumbnail?.download_url || fileMeta?.thumbnail?.url || fileMeta?.thumbnail_url || ''
      ).trim();
    }

    function isThumbnailUrl(url, fileMeta, infosThumbnail = '') {
      const candidate = String(url || '').trim();
      if (!candidate) return true;
      const known = knownThumbnailUrl(fileMeta);
      if (known && candidate === known) return true;
      if (infosThumbnail && candidate === infosThumbnail) return true;
      return /\/(?:thumbnails?|thumbs?)(?:\/|$)/i.test(candidate);
    }

    async function fetchBlob(url, { sameOrigin = false, timeoutMs = 45000 } = {}) {
      const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      const timer = controller ? window.setTimeout(() => controller.abort(), timeoutMs) : null;
      try {
        const response = await fetch(url, {
          method: 'GET',
          mode: 'cors',
          credentials: sameOrigin ? 'same-origin' : 'omit',
          cache: 'no-store',
          signal: controller?.signal,
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        if (!blob || blob.size === 0) throw new Error('Fichier vide');
        return blob;
      } catch (error) {
        if (error?.name === 'AbortError') throw new Error('Délai dépassé lors du téléchargement');
        throw error;
      } finally {
        if (timer) window.clearTimeout(timer);
      }
    }

    async function fileInfos(original, cropTransparent) {
      const infosUrl = new URL('/apps/easy-order/file/infos', window.location.origin);
      infosUrl.searchParams.set('filename', original);
      infosUrl.searchParams.set('crop_transparent_area', cropTransparent ? 'true' : 'false');
      const response = await fetch(infosUrl.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const text = await response.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch (_) {
        json = null;
      }
      if (!response.ok) {
        throw new Error(json?.message || json?.STATUS_DESCRIPTION || `infos HTTP ${response.status}`);
      }
      if (json?.STATUS_CODE && Number(json.STATUS_CODE) !== 200) {
        throw new Error(json.STATUS_DESCRIPTION || 'Erreur file infos');
      }
      return {
        fileUrl: String(json?.FILE_URL || original || '').trim(),
        pngFullSize: String(json?.FILE_PNG_FULL_SIZE || '').trim(),
        fileCropped: String(json?.FILE_CROPPED || '').trim(),
        thumbnail: String(json?.FILE_THUMBNAIL || '').trim(),
      };
    }

    async function tryBlobFromUrl(url, fileMeta, infosThumbnail, label) {
      const clean = String(url || '').trim();
      if (!clean) throw new Error(`${label} manquant`);
      if (isThumbnailUrl(clean, fileMeta, infosThumbnail)) {
        throw new Error(`${label} pointe vers un thumbnail`);
      }
      return fetchBlob(clean);
    }

    async function resolveLocalFile(fileMeta, fallbackName, fallbackType) {
      const filename = filenameOf(fileMeta) || fallbackName || 'fichier.png';
      const contentType = String(fileMeta?.content_type || fallbackType || '');
      const sourceUrl = originalUrl(fileMeta);
      if (!sourceUrl) throw new Error('Le fichier original est temporairement indisponible.');

      const ext = String(filename).split('.').pop().toLowerCase();
      const isPdfOrAi =
        ext === 'pdf' ||
        ext === 'ai' ||
        contentType.includes('pdf') ||
        contentType.includes('postscript') ||
        contentType.includes('illustrator');
      let lastError = null;

      try {
        const blob = await tryBlobFromUrl(sourceUrl, fileMeta, '', 'download_url');
        return {
          blob,
          filename,
          contentType: blob.type || contentType || 'application/octet-stream',
        };
      } catch (error) {
        lastError = error;
      }

      const cropOrder = isPdfOrAi ? [false, true] : [true, false];
      for (const crop of cropOrder) {
        try {
          const infos = await fileInfos(sourceUrl, crop);
          const candidates = [
            { url: infos.fileUrl, filename, contentType },
            { url: sourceUrl, filename, contentType },
            {
              url: infos.pngFullSize,
              filename: filename.replace(/\.[^.]+$/, '') + '.png',
              contentType: 'image/png',
            },
            {
              url: infos.fileCropped,
              filename: filename.replace(/\.[^.]+$/, '') + '.png',
              contentType: 'image/png',
            },
          ];
          for (const candidate of candidates) {
            try {
              const blob = await tryBlobFromUrl(candidate.url, fileMeta, infos.thumbnail, 'url serveur');
              return {
                blob,
                filename: candidate.filename || filename,
                contentType: blob.type || candidate.contentType || 'application/octet-stream',
              };
            } catch (error) {
              lastError = error;
            }
          }
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error('Import impossible');
    }

    async function addToEditor(card) {
      if (!config.onImport) {
        config.toast('Import non configuré', 'error');
        return;
      }
      const key = card?.dataset.fileKey || '';
      const fallbackName = card?.dataset.filename || 'fichier.png';
      const fallbackType = card?.dataset.contentType || '';
      const fileMeta = state.files.find((item) => String(item?.key || '') === key);
      if (!fileMeta) {
        setStatus('Fichier introuvable dans la bibliothèque.', 'error');
        config.toast('Fichier introuvable', 'error');
        return;
      }

      card.classList.add('is-busy');
      setStatus(`Import de ${fallbackName}…`, 'loading');
      close();

      try {
        const resolved = await resolveLocalFile(fileMeta, fallbackName, fallbackType);
        const type = resolved.contentType || 'application/octet-stream';
        const localFile = new File([resolved.blob], resolved.filename || fallbackName, { type });
        await config.onImport(localFile, { fileMeta, resolved });
      } catch (error) {
        console.error('[MesFichiersLibrary] add error:', error);
        const message = error?.message || 'Impossible d’ajouter ce fichier';
        config.toast(message, 'error');
        open();
        setStatus(message, 'error');
      } finally {
        card.classList.remove('is-busy');
      }
    }

    function onKeydown(event) {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) {
        close();
      }
    }

    function bind() {
      options.openButton?.addEventListener('click', open);
      overlay.addEventListener('click', close);
      refs.closeButtons.forEach((btn) => btn.addEventListener('click', () => close()));
      refs.search?.addEventListener('input', () => renderGrid());
      refs.retry?.addEventListener('click', () => loadFiles({ force: true }));
      refs.uploadTrigger?.addEventListener('click', () => refs.uploadInput?.click());
      refs.uploadInput?.addEventListener('change', (event) => {
        const files = event.target.files;
        event.target.value = '';
        uploadFiles(files);
      });
      document.addEventListener('keydown', onKeydown);
    }

    const api = {
      open,
      close,
      loadFiles,
      uploadFile,
      destroy() {
        document.removeEventListener('keydown', onKeydown);
        overlay.__mfLibraryBound = null;
      },
    };

    bind();
    overlay.__mfLibraryBound = api;
    return api;
  }

  window.MesFichiersLibrary = { mount };
})();
